import { useState, useEffect } from 'react';
import { useETagResource } from '../hooks/useETagPolling';

interface Project {
  id: string;
  address: string;
  status: string;
  createdAt: string;
  updatedAt: string;
}

interface ReportRequest {
  id: string;
  status: string;
  reportUrl?: string;
  requestedAt: string;
}

interface ManualBid {
  id: string;
  contractorName: string;
  priceCents: number;
  paymentTerms: string;
  timeline: string;
  startDate?: string;
  notes?: string;
  attachmentUrl?: string;
  isWinner: boolean;
  createdAt: string;
}

interface HomeownerDashboardProps {
  projectId: string;
  adminView?: boolean;
}

// COREBIDS FIX: homeowner dashboard with ETag polling for real-time updates
export default function HomeownerDashboard({ projectId, adminView = false }: HomeownerDashboardProps) {
  // CB:BEGIN dashboard-initial-loading
  const [initialLoading, setInitialLoading] = useState(true);
  // CB:END dashboard-initial-loading
  
  // Use ETag polling for real-time updates
  const { 
    data: projectData, 
    loading, 
    error, 
    lastUpdated 
  } = useETagResource({
    url: `/api/projects/${projectId}`,
    interval: 15000, // Poll every 15 seconds
    enabled: !!projectId,
    onUpdate: (data) => {
      console.log('🔄 Project data updated:', data);
    },
    onError: (err) => {
      console.error('❌ ETag polling error:', err);
    }
  });

  const project = projectData?.project || null;
  const reportRequest = projectData?.reportRequest || null;
  const manualBids = projectData?.manualBids || [];

  // CB:BEGIN dashboard-initial-loading-timer
  // Clear initialLoading as soon as the ETag resource resolves (data or error) so the
  // status badge and any banners are correct on first render — no post-load flash.
  useEffect(() => {
    if (!loading || error) {
      setInitialLoading(false);
    }
  }, [loading, error]);
  // CB:END dashboard-initial-loading-timer

  const getStatusDisplay = (status: string) => {
    // CB:BEGIN active-status-display
    // Show ACTIVE instead of CLOSED when bid is accepted (project is actively being worked on)
    const displayStatus = status === 'CLOSED' ? 'ACTIVE' : status;
    // CB:END active-status-display
    
    const statusMap: Record<string, { label: string; color: string }> = {
      'REQUESTED': { label: 'Request Submitted', color: 'bg-blue-100 text-blue-800' },
      'REPORT_ORDERED': { label: 'Report Ordered', color: 'bg-yellow-100 text-yellow-800' },
      'BIDS_COLLECTING': { label: 'Collecting Bids', color: 'bg-orange-100 text-orange-800' },
      'BIDS_READY': { label: 'Bids Ready', color: 'bg-green-100 text-green-800' },
      'BID_SELECTED': { label: 'Bid Selected', color: 'bg-purple-100 text-purple-800' },
      'ACTIVE': { label: 'Project Active', color: 'bg-indigo-100 text-indigo-800' },
      'CLOSED': { label: 'Project Active', color: 'bg-indigo-100 text-indigo-800' }, // CB:BEGIN map-closed-to-active - Map CLOSED to ACTIVE display
      'COMPLETED': { label: 'Completed', color: 'bg-gray-100 text-gray-800' }
    };
    
    return statusMap[displayStatus] || { label: displayStatus, color: 'bg-gray-100 text-gray-800' };
  };

  const formatPrice = (priceCents: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(priceCents / 100);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // CB:BEGIN dashboard-initial-loading-screen
  if (initialLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 text-base">Loading dashboard</p>
        </div>
      </div>
    );
  }
  // CB:END dashboard-initial-loading-screen

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-800">Error: {error}</p>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <p className="text-gray-800">Project not found</p>
      </div>
    );
  }

  const statusDisplay = getStatusDisplay(project.status);

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      {/* COREBIDS FIX: project header with status */}
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-2xl font-bold text-gray-900">
            {adminView ? 'Project Overview' : 'Your Project'}
          </h1>
          <span className={`px-3 py-1 rounded-full text-sm font-medium ${statusDisplay.color}`}>
            {statusDisplay.label}
          </span>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <h3 className="text-sm font-medium text-gray-500">Address</h3>
            <p className="text-lg text-gray-900">{project.address}</p>
          </div>
          <div>
            <h3 className="text-sm font-medium text-gray-500">Created</h3>
            <p className="text-lg text-gray-900">{formatDate(project.createdAt)}</p>
          </div>
        </div>
      </div>

      {/* COREBIDS FIX: report status section */}
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Report Status</h2>
        
        {reportRequest ? (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">
                Report requested on {formatDate(reportRequest.requestedAt)}
              </span>
              <span className={`px-2 py-1 rounded text-xs font-medium ${
                reportRequest.status === 'RECEIVED' 
                  ? 'bg-green-100 text-green-800' 
                  : 'bg-yellow-100 text-yellow-800'
              }`}>
                {reportRequest.status}
              </span>
            </div>
            
            {reportRequest.reportUrl && (
              <div className="mt-3">
                <a 
                  href={reportRequest.reportUrl} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                >
                  View Report
                </a>
              </div>
            )}
          </div>
        ) : (
          <p className="text-gray-500">No report request found</p>
        )}
      </div>

      {/* COREBIDS FIX: bids section */}
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Contractor Bids</h2>
        
        {manualBids.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-gray-500 mb-2">Collecting bids...</p>
            <div className="flex justify-center space-x-2">
              <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce"></div>
              <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
              <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
            </div>
          </div>
        ) : manualBids.length < 3 ? (
          <div className="space-y-4">
            {manualBids.map((bid) => (
              <div key={bid.id} className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-medium text-gray-900">{bid.contractorName}</h3>
                  <span className="text-lg font-bold text-gray-900">{formatPrice(bid.priceCents)}</span>
                </div>
                <p className="text-sm text-gray-600">{bid.paymentTerms}</p>
                <p className="text-sm text-gray-600">Timeline: {bid.timeline}</p>
                {bid.startDate && (
                  <p className="text-sm text-gray-600">Start: {formatDate(bid.startDate)}</p>
                )}
                {bid.notes && (
                  <p className="text-sm text-gray-600 mt-2">{bid.notes}</p>
                )}
              </div>
            ))}
            <div className="text-center py-4">
              <p className="text-gray-500">Waiting for more bids...</p>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {manualBids.map((bid) => (
              <div 
                key={bid.id} 
                className={`border rounded-lg p-4 ${
                  bid.isWinner 
                    ? 'border-green-500 bg-green-50' 
                    : 'border-gray-200 bg-white'
                }`}
              >
                {bid.isWinner && (
                  <div className="flex items-center mb-2">
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      Selected
                    </span>
                  </div>
                )}
                
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-medium text-gray-900">{bid.contractorName}</h3>
                  <span className="text-lg font-bold text-gray-900">{formatPrice(bid.priceCents)}</span>
                </div>
                
                <div className="space-y-1 text-sm text-gray-600">
                  <p><strong>Payment:</strong> {bid.paymentTerms}</p>
                  <p><strong>Timeline:</strong> {bid.timeline}</p>
                  {bid.startDate && (
                    <p><strong>Start:</strong> {formatDate(bid.startDate)}</p>
                  )}
                </div>
                
                {bid.notes && (
                  <div className="mt-3">
                    <p className="text-sm text-gray-600">{bid.notes}</p>
                  </div>
                )}
                
                {bid.attachmentUrl && (
                  <div className="mt-3">
                    <a 
                      href={bid.attachmentUrl} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-sm text-blue-600 hover:text-blue-800"
                    >
                      View Attachment
                    </a>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

